import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { SheetContentType, useSheetStore } from "./sheet.store";

const ARTICLE_PARAM = "id";

/**
 * Mirrors the topmost open article sheet into the `id` search param so
 * the URL is shareable, and opens the sheet on load when the param is present.
 */
export const ArticleSheetUrlSync: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const openSheet = useSheetStore((state) => state.openSheet);
	const articleId = useSheetStore((state) => {
		for (let i = state.sheets.length - 1; i >= 0; i--) {
			const sheet = state.sheets[i];
			if (sheet.sheetContentType === SheetContentType.ARTICLE) {
				return sheet.props?.articleId as number | undefined;
			}
		}
		return undefined;
	});

	const initialParamRef = useRef(searchParams.get(ARTICLE_PARAM));
	useEffect(() => {
		const id = Number(initialParamRef.current);
		if (!initialParamRef.current || Number.isNaN(id)) return;
		const alreadyOpen = useSheetStore
			.getState()
			.sheets.some(
				(sheet) =>
					sheet.sheetContentType === SheetContentType.ARTICLE &&
					sheet.props?.articleId === id
			);
		if (!alreadyOpen) {
			openSheet(SheetContentType.ARTICLE, { articleId: id });
		}
	}, [openSheet]);

	useEffect(() => {
		const current = searchParams.get(ARTICLE_PARAM);
		const next = articleId != null ? String(articleId) : null;
		if (current === next) return;
		setSearchParams(
			(prev) => {
				if (next === null) {
					prev.delete(ARTICLE_PARAM);
				} else {
					prev.set(ARTICLE_PARAM, next);
				}
				return prev;
			},
			{ replace: true }
		);
	}, [articleId, searchParams, setSearchParams]);

	return null;
};
